import {
  useResponsiveFontSize,
  useResponsiveHeight,
  useResponsiveWidth,
} from '@/components/Util/reactResponsiveDimensions';
import Icon from '@expo/vector-icons/FontAwesome';
import { setStringAsync } from 'expo-clipboard';
import { ReactNode, useMemo } from 'react';
import { SafeAreaView, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import Modal from 'react-native-modal';

import colors, { appTheme } from '@/config/colors';

import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { useOverlayGuard } from '@/hooks/useOverlayGuard';
import { allowNativeDriver } from '@/utils/allowNativeDriver';
import { IS_WEB } from '@/utils/platform';

const styles = StyleSheet.create({
  button: {
    width: '50%',
  },
  touchableButton: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonColorCancel: {
    backgroundColor: colors.grey,
  },
  buttonColorDisable: {
    backgroundColor: colors.darkerGrey,
  },
  buttonColorConfirm: {
    backgroundColor: appTheme.primary,
  },
  textContentContainer: {
    // justifyContent: 'center',
    // alignItems: 'center',
  },
  backgroundWhite: {
    backgroundColor: 'white',
  },
  borderBottom: {
    borderBottomColor: colors.grey,
    borderBottomWidth: 2,
  },
  buttonTextConfirm: {
    color: colors.white,
  },
  buttonTextCancel: {
    color: colors.darkGrey,
  },
  buttonWhiteTextCancel: {
    color: colors.white,
  },
  copyButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconStyle: { alignSelf: 'center' },
  redBackground: {
    backgroundColor: colors.red,
  },
  greenBackground: {
    backgroundColor: appTheme.primary,
  },
  full: { flex: 1 },
  safeAreaView: { flex: 1, backgroundColor: 'white' },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  userSelect: {
    userSelect: 'none',
  },
});

interface CustomTextComponentProps {
  text: string;
  canBeCopied?: boolean;
}

function CustomTextComponent({ text, canBeCopied }: CustomTextComponentProps) {
  // eslint-disable-next-line
  const responsiveStyles = StyleSheet.create({
    textContent: {
      fontSize: useResponsiveFontSize(3),
      color: appTheme.textColor,
      padding: useResponsiveWidth(2.8),
    },
    touchToCopyText: {
      position: 'relative',
      fontSize: useResponsiveFontSize(0.8),
      color: colors.darkGrey,
    },
    emailText: {
      fontSize: useResponsiveFontSize(2),
      color: appTheme.textColor,
      fontWeight: '700',
      marginRight: 20,
    },
  });

  const { showNotification } = useNotification();

  const copyToClipboard = async () => {
    if (IS_WEB) {
      await navigator.clipboard.writeText(text);
    }
    await setStringAsync(text);
    showNotification({
      type: 'info',
      title: 'Ihr Code wurde kopiert',
    });
  };

  const iconSize = useResponsiveFontSize(2);

  return canBeCopied ? (
    <PressableNeolexon
      onPress={async () => await copyToClipboard()}
      style={styles.copyButton}
    >
      <Text style={[responsiveStyles.emailText]}>{text}</Text>
      <Icon
        name={'clipboard'}
        color={colors.darkPurpleBlue}
        size={iconSize}
        style={styles.iconStyle}
      />
    </PressableNeolexon>
  ) : (
    <Text style={responsiveStyles.emailText}>{text}</Text>
  );
}

interface ConfirmModalProps {
  isVisible: boolean;
  headerText: string;
  contentText: string | ReactNode;
  cancelAction: () => void;
  cancelText: string;
  confirmAction: () => void;
  confirmText: string;
  hasEmail: boolean;
  emailText: string;
  longContent: string;
  verticalAlignButtons: boolean;
  emailCanBeCopied: boolean;
  children: ReactNode;
  redBackgroundConfirmButton: boolean;
  greenBackgroundCancelButton: boolean;
  isContentComponent: boolean;
  customHeight: number;
  isConfirmActionDisabled: boolean;
  confirmTextBackgroundColor: string;
  hasWebView: boolean;
  isFullscreen: boolean;
  customHeaderSize: number;
  noBackground: boolean;
  hasBorder: boolean;
  minimized: boolean;
}

function ConfirmModal({
  isVisible,
  headerText,
  contentText,
  cancelAction,
  cancelText,
  confirmAction,
  confirmText,
  hasEmail,
  emailText,
  longContent,
  verticalAlignButtons,
  emailCanBeCopied,
  children,
  redBackgroundConfirmButton,
  greenBackgroundCancelButton,
  isContentComponent,
  customHeight,
  isConfirmActionDisabled,
  confirmTextBackgroundColor,
  hasWebView,
  isFullscreen,
  customHeaderSize,
  noBackground,
  hasBorder,
  minimized,
}: Partial<ConfirmModalProps>) {
  useOverlayGuard(!!isVisible);
  const responsiveStyles = StyleSheet.create({
    touchableButton: {
      borderRadius: useResponsiveFontSize(0.2),
    },
    buttonText: {
      fontSize: isFullscreen
        ? useResponsiveHeight(6)
        : useResponsiveFontSize(1.5),
      fontWeight: '500',
      textAlign: 'center',
    },
    reduceConfirmButtonFontSize: {
      fontSize: confirmText
        ? useResponsiveFontSize(
            isFullscreen
              ? (3 / (confirmText?.split(' ').length || 1)) * 3.5 // Adjust the factor for fullscreen
              : (3 / (confirmText?.split(' ').length || 1)) * 2.3,
          )
        : 3,
    },
    reduceCancelButtonFontSize: {
      fontSize: cancelText
        ? useResponsiveFontSize(
            isFullscreen
              ? (3 / (cancelText?.split(' ').length || 1)) * 3.5 // Adjust the factor for fullscreen
              : (3 / (cancelText?.split(' ').length || 1)) * 2.3,
          )
        : 3,
    },
    buttonHeight: {
      height: isFullscreen ? useResponsiveHeight(12) : useResponsiveHeight(6),
      margin: useResponsiveFontSize(0.8),
    },
    buttonConfirm: {
      marginLeft: useResponsiveWidth(0.2),
    },
    buttonCancel: {
      marginRight: useResponsiveWidth(0.2),
    },
    textContentContainer: {
      maxHeight: isFullscreen
        ? useResponsiveHeight(60)
        : useResponsiveHeight(
            customHeight ? customHeight : minimized ? 20 : 50,
          ),
    },
    textContent: {
      fontSize: isFullscreen
        ? useResponsiveFontSize(3.5)
        : minimized
          ? useResponsiveFontSize(1.5)
          : useResponsiveFontSize(2.5),
      color: appTheme.textColor,
      padding: useResponsiveWidth(2.8),
    },
    emailText: {
      fontSize: useResponsiveFontSize(3),
      color: appTheme.textColor,
      fontWeight: '700',
    },
    headerText: {
      fontSize: isFullscreen
        ? useResponsiveFontSize(customHeaderSize) || useResponsiveFontSize(3.5)
        : useResponsiveFontSize(2),
      textAlign: 'center',
      color: colors.darkGrey,
      backgroundColor: colors.white,
      padding: useResponsiveHeight(1.5),
    },
    longContentText: {
      fontSize: useResponsiveFontSize(2.5),
      color: appTheme.textColor,
      padding: useResponsiveWidth(3.5),
    },
  });

  const borderStyle = useMemo(() => {
    return {
      borderWidth: hasBorder ? 1 : 0,
      borderColor: hasBorder ? colors.black : 'transparent',
      borderRadius: hasBorder ? 6 : 0,
    };
  }, []);

  return (
    <Modal
      avoidKeyboard
      isVisible={isVisible || false}
      animationIn={'fadeInDown'}
      animationOut={'fadeOutUp'}
      useNativeDriver={allowNativeDriver()}
      useNativeDriverForBackdrop={allowNativeDriver()}
      hideModalContentWhileAnimating
      supportedOrientations={['landscape', 'landscape-left', 'landscape-right']}
      backdropOpacity={noBackground ? 0 : 0.7}
      statusBarTranslucent
    >
      {hasWebView ? (
        <SafeAreaView style={styles.full}>
          <View style={styles.safeAreaView}>
            <View style={styles.center}>
              {children}
              <View
                style={[
                  styles.button,
                  !cancelText || verticalAlignButtons ? { width: '100%' } : {},
                ]}
              >
                <PressableNeolexon
                  style={[
                    styles.touchableButton,
                    styles.buttonColorConfirm,
                    responsiveStyles.buttonHeight,
                    cancelText ? responsiveStyles.buttonConfirm : undefined,
                    responsiveStyles.touchableButton,
                    redBackgroundConfirmButton && styles.redBackground,
                    isConfirmActionDisabled && styles.buttonColorDisable,
                    confirmTextBackgroundColor
                      ? {
                          backgroundColor: confirmTextBackgroundColor,
                        }
                      : undefined,
                  ]}
                  onPress={confirmAction}
                  disabled={isConfirmActionDisabled}
                >
                  {!!confirmText && (
                    <Text
                      style={[
                        responsiveStyles.buttonText,
                        styles.buttonTextConfirm,
                        confirmText?.split(' ').length > 2 &&
                          responsiveStyles.reduceConfirmButtonFontSize,
                      ]}
                    >
                      {confirmText}
                    </Text>
                  )}
                </PressableNeolexon>
              </View>
            </View>
          </View>
        </SafeAreaView>
      ) : null}
      {!hasWebView && (
        <View style={[styles.backgroundWhite, borderStyle]}>
          {!!headerText && (
            <View style={styles.borderBottom}>
              <Text style={[responsiveStyles.headerText, styles.userSelect]}>
                {headerText}
              </Text>
            </View>
          )}

          <ScrollView
            style={[
              responsiveStyles.textContentContainer,
              !isContentComponent && styles.textContentContainer,
            ]}
            alwaysBounceVertical={false}
          >
            {isContentComponent ? contentText : null}
            {!isContentComponent && contentText ? (
              <Text
                style={[
                  responsiveStyles.textContent,
                  longContent ? responsiveStyles.longContentText : {},
                ]}
              >
                {contentText}
              </Text>
            ) : null}
            {hasEmail && !emailCanBeCopied && !!emailText ? (
              <CustomTextComponent
                canBeCopied={emailCanBeCopied}
                text={emailText}
              />
            ) : null}
            {hasEmail && emailCanBeCopied && !!emailText ? (
              <CustomTextComponent
                canBeCopied={emailCanBeCopied}
                text={emailText}
              />
            ) : null}
            {!!children && children}
          </ScrollView>
          <View
            style={[{ flexDirection: verticalAlignButtons ? 'column' : 'row' }]}
          >
            {!!cancelText && (
              <View
                style={[
                  styles.button,
                  verticalAlignButtons ? { width: '100%' } : {},
                ]}
              >
                <PressableNeolexon
                  style={[
                    styles.touchableButton,
                    styles.buttonColorCancel,
                    responsiveStyles.buttonHeight,
                    !verticalAlignButtons && responsiveStyles.buttonCancel,
                    responsiveStyles.touchableButton,
                    greenBackgroundCancelButton && styles.greenBackground,
                  ]}
                  onPress={cancelAction}
                >
                  <Text
                    style={[
                      responsiveStyles.buttonText,
                      greenBackgroundCancelButton
                        ? styles.buttonWhiteTextCancel
                        : styles.buttonTextCancel,
                      cancelText?.split(' ').length > 2 &&
                        responsiveStyles.reduceCancelButtonFontSize,
                    ]}
                  >
                    {cancelText}
                  </Text>
                </PressableNeolexon>
              </View>
            )}
            <View
              style={[
                styles.button,
                !cancelText || verticalAlignButtons ? { width: '100%' } : {},
              ]}
            >
              <PressableNeolexon
                style={[
                  styles.touchableButton,
                  styles.buttonColorConfirm,
                  responsiveStyles.buttonHeight,
                  cancelText && !verticalAlignButtons
                    ? responsiveStyles.buttonConfirm
                    : undefined,
                  responsiveStyles.touchableButton,
                  redBackgroundConfirmButton ? styles.redBackground : undefined,
                  isConfirmActionDisabled && styles.buttonColorDisable,
                  confirmTextBackgroundColor
                    ? {
                        backgroundColor: confirmTextBackgroundColor,
                      }
                    : undefined,
                ]}
                onPress={confirmAction}
                disabled={isConfirmActionDisabled}
              >
                {!!confirmText && (
                  <Text
                    style={[
                      responsiveStyles.buttonText,
                      styles.buttonTextConfirm,
                      confirmText?.split(' ').length > 2 &&
                        responsiveStyles.reduceConfirmButtonFontSize,
                    ]}
                  >
                    {confirmText}
                  </Text>
                )}
              </PressableNeolexon>
            </View>
          </View>
        </View>
      )}
    </Modal>
  );
}

export default ConfirmModal;
